import { BoltWhiteIcon } from "@/assets/icons";
import EditIcon from "@/assets/icons/Edit.svg?react";
import ShareIcon from "@/assets/icons/share_filled.svg?react";
import ErrorOrNotFoundPage from "@/components/errors/ErrorOrNotFoundPage";
import { CreatorLink } from "@/components/links/CreatorLink";
import {
  useIsApiConfigured,
  useLoggedInUser,
} from "@/components/providers/ApiProvider/hooks";
import { useCartManager } from "@/components/providers/CartProvider";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import ExclusiveReleaseBadge from "@/components/ui/ExclusiveReleaseBadge";
import SuperSingleBadge from "@/components/ui/SuperSingleBadge";
import HeartButton from "@/components/ui/HeartButton";
import InAppBrowserDownloadNotice from "@/components/ui/InAppBrowserDownloadNotice";
import CurrencyInput, {
  validatePrice,
} from "@/components/ui/form/CurrencyInput";
import { useEmailVerificationStatus } from "@/hooks/useEmailVerificationStatus";
import { useFulfillCheckoutSession } from "@/hooks/useFulfillCheckoutSession";
import {
  getCreatorDisplayName,
  getIsExclusive,
  getIsMasteredOverride,
  getPriceIntervalOverride,
} from "@/lib/config/album-overrides";
import { analytics, EventEnum } from "@/lib/analytics";
import { useAlbumsControllerAlbumDetail } from "@/lib/lutely-api/gen/albums/albums";
import { TracksControllerListClassificationsTypesItem } from "@/lib/lutely-api/gen/dtos";
import { usePurchaseControllerGetAlbumOrders } from "@/lib/lutely-api/gen/purchase/purchase";
import { useTracksControllerListClassifications } from "@/lib/lutely-api/gen/tracks/tracks";
import {
  albumReleaseType,
  albumTrackCountLabel,
  albumTypeLabel,
  isNewRelease,
} from "@/lib/utils";
import { downloadAlbum } from "@/lib/utils/album-utils";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Avatar,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";

import { useShare } from "@/components/share/useShare";
import { SITE_URL } from "@/lib/config/env-var.config";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import ScrollingTitle from "@/components/ui/ScrollingTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineDownload, HiOutlineShoppingBag } from "react-icons/hi";
import { MdMovie, MdOutlineAlbum } from "react-icons/md";
import AlbumDetailsTab from "./AlbumDetailsTab";
import AlbumLyricsDrawer from "./AlbumLyricsDrawer";
import AlbumTracklist from "./AlbumTracklist";
import PreReleaseBlock from "./PreReleaseBlock";
import VideoPlayerModal from "./VideoPlayerModal";

/**
 * Compute 3 preset price tiers for PWYW albums.
 * The first tier is the minimum price itself.
 * The remaining tiers are the next multiples of the configured increment
 * above the minimum price.
 */
const PRICE_MULTIPLE = 5;
const SHOW_PRICES = 3;
function getPresetTiers(minPrice: number): number[] {
  const toReturn: number[] = [];
  toReturn.push(minPrice);
  let nextTier = Math.ceil((minPrice + 0.01) / PRICE_MULTIPLE) * PRICE_MULTIPLE;
  while (toReturn.length < SHOW_PRICES) {
    if (nextTier > minPrice) {
      toReturn.push(nextTier);
    }
    nextTier += PRICE_MULTIPLE;
  }
  return toReturn;
}

/**
 * Compute preset price tiers from a fixed dollar interval.
 * The first tier is always the album's exact current price; each subsequent
 * tier adds the interval and is rounded to a whole dollar (e.g. a $19.99 album
 * with an interval of 5 yields [19.99, 25, 30]).
 */
function getPresetTiersFromInterval(
  minPrice: number,
  interval: number,
): number[] {
  return Array.from({ length: SHOW_PRICES }, (_, index) =>
    index === 0
      ? minPrice
      : Math.max(minPrice, Math.round(minPrice + interval * index)),
  );
}

function formatTotalDuration(tracks: { duration?: number | null }[]): string {
  const totalSeconds = tracks.reduce((sum, t) => sum + (t.duration || 0), 0);
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function PublicAlbumPage() {
  const { ALBUM_ID, CREATOR_SLUG, ALBUM_SLUG } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const sessionId =
    typeof search.session_id === "string" ? search.session_id : undefined;

  const {
    data: albumData,
    isSuccess,
    isError,
    refetch: refetchAlbumData,
  } = useAlbumsControllerAlbumDetail(
    {
      albumId: ALBUM_ID,
      creatorSlug: CREATOR_SLUG,
      albumSlug: ALBUM_SLUG,
    },
    {
      query: {
        enabled: !!ALBUM_ID || (!!CREATOR_SLUG && !!ALBUM_SLUG),
        retry: false,
      },
    },
  );

  const { addToCart, onItemExists, onToggleCart } = useCartManager();
  const isInCart = albumData?.albumId ? onItemExists(albumData.albumId) : false;

  // This page is public, so most visitors are anonymous. Album orders are an
  // authenticated read — firing it without a session just 403s (and retries),
  // so it waits until the API client has a token to send.
  const isApiConfigured = useIsApiConfigured();
  const { data: albumOrders, refetch: refetchAlbumOrders } =
    usePurchaseControllerGetAlbumOrders({
      // `enabled` gates the automatic fetch but not refetch(), so a 403 can
      // still reach this query. The QueryClient default declines to retry those.
      query: { enabled: isApiConfigured },
    });
  const albumOrder = useMemo(() => {
    if (!albumData?.albumId || !albumOrders) return null;
    return albumOrders.find(
      (order) => order.album.albumId === albumData.albumId,
    );
  }, [albumData?.albumId, albumOrders]);

  const handleCheckoutSuccess = useCallback(() => {
    // Stay on the current album page (branded or ID route) and strip the
    // checkout search params.
    navigate({
      to: ".",
      search: (prev) => {
        const nextSearch = { ...(prev as Record<string, unknown>) };
        delete nextSearch.session_id;
        delete nextSearch["open-cart"];
        return nextSearch;
      },
      replace: true,
    });
  }, [navigate]);

  const handleCheckoutSettled = useCallback(() => {
    // refetch() ignores `enabled`, so this needs its own guard.
    if (!isApiConfigured) return;
    refetchAlbumOrders();
  }, [isApiConfigured, refetchAlbumOrders]);

  useFulfillCheckoutSession({
    sessionId,
    enabled: !!ALBUM_ID || (!!CREATOR_SLUG && !!ALBUM_SLUG),
    onSuccess: handleCheckoutSuccess,
    onSettled: handleCheckoutSettled,
  });

  const isOwned = !!albumOrder;
  const [, setForceUpdate] = useState(0);
  const handlePreReleaseExpired = useCallback(
    () => setForceUpdate((n) => n + 1),
    [],
  );
  const isPreRelease =
    !!albumData?.releaseDate && parseISO(albumData.releaseDate) > new Date();
  const purchaseDate = albumOrder?.createdAt
    ? format(parseISO(albumOrder.createdAt), "MM/dd/yyyy")
    : null;

  const { data: classificationsData } = useTracksControllerListClassifications({
    types: [...Object.values(TracksControllerListClassificationsTypesItem)],
  });

  const { isLoading, isVerified } = useEmailVerificationStatus();
  const { data: currentUser, isSuccess: userLoaded } = useLoggedInUser();

  // Whether the user may edit this album (admin or same label as the creator)
  // is computed by the API and returned on the album detail. Drives the
  // discreet "Edit in Audiolab" shortcut below.
  const canEditAlbum = albumData?.canEdit ?? false;

  const genreDisplayText = useMemo(() => {
    if (!albumData?.genreId || !classificationsData) return null;

    const classificationMap = new Map<string, string>();
    for (const c of classificationsData) {
      classificationMap.set(c.classificationId, c.name);
    }

    const parts: string[] = [];
    const genreName = classificationMap.get(albumData.genreId);
    if (genreName) parts.push(genreName);

    for (const subGenreId of albumData.subGenreIds || []) {
      const subGenreName = classificationMap.get(subGenreId);
      if (subGenreName) parts.push(subGenreName);
    }

    return parts.length > 0 ? parts.join(", ") : null;
  }, [albumData?.genreId, albumData?.subGenreIds, classificationsData]);

  const [lyricsFor, setLyricsFor] = useState<{
    trackId: string;
    clean: boolean;
  } | null>(null);

  const lyricsTrackTitle = useMemo(() => {
    if (!lyricsFor || !albumData) return "";
    const track = albumData.tracks.find((t) => t.trackId === lyricsFor.trackId);
    const title = track?.title || "Untitled";
    return lyricsFor.clean ? `${title} (Clean)` : title;
  }, [lyricsFor, albumData]);

  const handleLyricsClick = useCallback((trackId: string, clean?: boolean) => {
    setLyricsFor({ trackId, clean: !!clean });
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);
  const [videoModal, setVideoModal] = useState<{
    src: string;
    title: string;
  } | null>(null);

  // PWYW state
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customInputFocused, setCustomInputFocused] = useState(false);
  const [customPriceValue, setCustomPriceValue] = useState("");
  const [customPriceError, setCustomPriceError] = useState<string | null>(null);

  const minPrice = albumData?.price ?? 0;
  const presetTiers = useMemo(() => {
    const interval = albumData?.albumId
      ? getPriceIntervalOverride(albumData.albumId)
      : undefined;
    return interval !== undefined
      ? getPresetTiersFromInterval(minPrice, interval)
      : getPresetTiers(minPrice);
  }, [albumData?.albumId, minPrice]);

  const handleSelectTier = (price: number) => {
    setSelectedPrice(price);
    setIsCustomMode(false);
    setCustomInputFocused(false);
    setCustomPriceValue("");
    setCustomPriceError(null);
  };

  const handleEnterCustomMode = () => {
    setIsCustomMode(true);
    setCustomInputFocused(true);
    setSelectedPrice(null);
  };

  const handleCustomPriceChange = (value: string) => {
    setCustomPriceValue(value);
    if (customPriceError) setCustomPriceError(null);

    if (value !== "") {
      const num = parseFloat(value);
      const error = validatePrice(value, minPrice);
      if (!error && !isNaN(num)) {
        setSelectedPrice(num);
      } else {
        setSelectedPrice(null);
      }
    } else {
      setSelectedPrice(null);
    }
  };

  const handleCustomPriceBlur = () => {
    setCustomInputFocused(false);
    if (customPriceValue !== "") {
      const error = validatePrice(customPriceValue, minPrice);
      setCustomPriceError(error);
    }
  };

  const handleCustomPillClick = () => {
    setCustomInputFocused(true);
  };

  const handleAddToCart = () => {
    if (!albumData?.albumId) return;

    if (isInCart) {
      onToggleCart();
      return;
    }

    if (selectedPrice == null) return;
    addToCart({
      type: "album",
      albumId: albumData.albumId,
      price: selectedPrice,
      album: albumData,
      chosenPrice: selectedPrice,
    });
    analytics.track(EventEnum.ADDED_TO_CART, {
      albumId: albumData.albumId,
      price: selectedPrice,
      trackTitle: albumData.title,
      artistName: albumData.creator?.stageName,
      imageUrl: albumData.artworkUrl,
    });
  };

  const { share, shareModal } = useShare();

  const handleDownload = useCallback(async () => {
    const albumOrderId = albumOrder?.albumOrderId;
    if (!albumOrderId || !albumData) return;

    setIsDownloading(true);
    try {
      await downloadAlbum(albumOrderId);
    } finally {
      setIsDownloading(false);
    }
  }, [albumOrder?.albumOrderId, albumData]);

  const albumNotFound = !albumData && (isSuccess || isError);

  useEffect(() => {
    if (ALBUM_ID && currentUser?.userId) {
      // Refetch album data when user changes, to get correct ownership status and movie, if available
      refetchAlbumData();
    }
  }, [currentUser?.userId, ALBUM_ID, refetchAlbumData]);

  if (albumNotFound) {
    return (
      <ContainerWrapper>
        <ErrorOrNotFoundPage title="Album not found" showReset={false} />
      </ContainerWrapper>
    );
  }

  if (!albumData) {
    return null;
  }

  const creatorDisplayName = getCreatorDisplayName(
    albumData.albumId,
    albumData.creator.stageName || "Unknown Artist",
  );

  const masteredOverride = getIsMasteredOverride(albumData.albumId);
  const isMastered = masteredOverride ?? albumData.hasMasteredLutelyTracks;
  const isExclusive = getIsExclusive(albumData.albumId);

  const showPurchaseBlock = albumData.price != null && albumData.publishedAt;

  const albumShareUrl =
    albumData.slug && albumData.creator.slug
      ? `${SITE_URL}/@${albumData.creator.slug}/${albumData.slug}`
      : window.location.href;

  const showingEmailBanner =
    userLoaded && currentUser?.email && !isLoading && !isVerified;
  const minHeight = showingEmailBanner
    ? "calc(100vh - 130px)"
    : "calc(100vh - 74px)";

  return (
    <ContainerWrapper
      style={{
        padding: 0,
        minHeight,
        ["--album-page-height" as string]: minHeight,
      }}
      className="relative overflow-hidden lg:overflow-hidden"
    >
      {/* Blurred album artwork background */}
      {albumData.artworkUrl && (
        <img
          src={albumData.artworkUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-3xl scale-110 brightness-[0.6]"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 to-black/80" />

      <div className="relative z-10 grid w-full grid-cols-1 lg:grid-cols-[620px_minmax(0,1fr)] xl:grid-cols-[750px_minmax(0,1fr)] items-start pt-5 lg:h-[var(--album-page-height)] lg:gap-6 px-4">
        {/* Left: Hero Artwork */}
        <div className="w-full lg:w-[620px] xl:w-[750px] lg:h-[var(--album-page-height)] lg:flex lg:items-center lg:justify-start lg:shrink-0">
          <div className="relative aspect-square overflow-hidden bg-background-tertiary w-full max-w-[750px] lg:w-[620px] xl:w-[750px] h-auto mx-auto lg:mx-0">
            {albumData.artworkUrl ? (
              <img
                src={albumData.artworkUrl}
                alt={albumData.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Flex
                className="w-full h-full items-center justify-center"
                align="center"
                justify="center"
              >
                <MdOutlineAlbum className="w-16 h-16 text-foreground-quarternary" />
              </Flex>
            )}
          </div>
        </div>

        {/* Right: Album Info + Tabs */}
        <Flex
          direction="column"
          className="gap-[9px] lg:gap-6 p-6 pb-[100px] lg:pl-8 lg:pr-4 lg:py-8 lg:h-[var(--album-page-height)] lg:overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.35)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/35 [&::-webkit-scrollbar-thumb:hover]:bg-white/50"
        >
          <Flex gap="3" align="center" justify="between">
            {albumReleaseType(albumData) === "SUPER_SINGLE" ? (
              <Flex align="center" gap="2">
                {isNewRelease(albumData) && (
                  <Text className="font-['Figtree'] text-[11px] tracking-[0.16em] uppercase font-semibold text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.55)]">
                    New
                  </Text>
                )}
                <SuperSingleBadge />
              </Flex>
            ) : (
              <Text
                className="font-['Figtree'] text-[11px] tracking-[0.16em] uppercase font-semibold text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.55)]"
                data-testid="album-type-label"
              >
                {isNewRelease(albumData) && "New "}
                {albumTypeLabel(albumData)}
              </Text>
            )}
            {/* Icon cluster (LUT-881): every album action lives up here on the
                "New Album" line — right under the cover on mobile — so the
                Name Your Price block fits above the fold. Like is a bare
                heart (no label, no pill), and Follow is gone from album pages
                outright: following is artist-specific, so it belongs to the
                artist profile alone. */}
            <Flex align="center" gap="2">
              {isExclusive && (
                <Tooltip content="Exclusive Release">
                  <div>
                    <ExclusiveReleaseBadge badgeOnly className="!h-7" />
                  </div>
                </Tooltip>
              )}
              {isMastered && (
                <Tooltip content="Mastered by Lutely">
                  <div className="flex items-center justify-center [&>svg]:!fill-[#43DDA7] [&>svg>path]:!fill-[#43DDA7]">
                    <BoltWhiteIcon className="w-[18px] h-[18px]" />
                  </div>
                </Tooltip>
              )}
              <HeartButton
                kind="album"
                id={albumData.albumId}
                iconSize={18}
                color="#FFFFFF"
                className="hover:bg-white/15"
              />
              {/* Share entry, icon only (LUT-982) */}
              {albumData.publishedAt ? (
                <Tooltip content="Share">
                  <button
                    type="button"
                    onClick={() =>
                      share({
                        url: albumShareUrl,
                        title: albumData.title,
                        kind: "album",
                        artistName: creatorDisplayName,
                        artworkUrl: albumData.artworkUrl,
                      })
                    }
                    aria-label="Share album"
                    data-testid="album-share-button"
                    className="flex-shrink-0 flex items-center rounded-full hover:bg-white/15 text-gray-100 h-[32px] px-1.5 cursor-pointer transition-colors"
                  >
                    <ShareIcon width={20} height={20} />
                  </button>
                </Tooltip>
              ) : (
                <Tooltip content="Publish first to share">
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled
                      aria-label="Share album"
                      data-testid="album-share-button"
                      className="flex-shrink-0 flex items-center rounded-full text-gray-100 h-[32px] px-1.5 opacity-40 cursor-not-allowed"
                    >
                      <ShareIcon width={20} height={20} />
                    </button>
                  </span>
                </Tooltip>
              )}
              {canEditAlbum && (
                <Tooltip content="Edit in Audiolab">
                  <IconButton
                    asChild
                    variant="ghost"
                    aria-label="Edit in Audiolab"
                    className="flex-shrink-0 rounded-full hover:bg-white/15 text-gray-100"
                  >
                    <Link
                      to="/audiolab/releases/$ALBUM_ID"
                      params={{ ALBUM_ID: albumData.albumId }}
                    >
                      <EditIcon width={20} height={20} />
                    </Link>
                  </IconButton>
                </Tooltip>
              )}
            </Flex>
          </Flex>
          {/* Title and Icons */}
          <Flex justify="between" align="start" className="gap-4">
            <ScrollingTitle
              title={albumData.title}
              className="font-['Figtree'] text-[36px] lg:text-[72px] font-bold leading-[110%] lg:leading-[100%] tracking-[-0.36px] lg:tracking-[-0.72px] text-white [text-shadow:0_6px_18px_rgba(0,0,0,0.6)]"
            />
            {/* TODO: Add isExplicit field to Album model and DetailAlbumDto, then conditionally render ExplicitIcon */}
          </Flex>

          {/* Creator */}
          <CreatorLink
            creator={albumData.creator}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Avatar
              src={albumData.creator.profilePictureUrl ?? undefined}
              fallback={albumData.creator.stageName?.[0]?.toUpperCase() || "A"}
              className="w-[28px] h-[28px] aspect-square rounded-full shadow-[0_8px_12px_-1px_rgba(0,15,48,0.14)]"
              style={{
                width: "28px",
                height: "28px",
                aspectRatio: "1/1",
                borderRadius: "999px",
                boxShadow: "0 8px 12px -1px rgba(0, 15, 48, 0.14)",
              }}
            />
            <Text className="font-['Figtree'] text-[18px] lg:text-[24px] font-semibold leading-[140%] lg:leading-[120%] text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.6)]">
              {creatorDisplayName}
            </Text>
          </CreatorLink>
          {/* Tracks info row — desktop only here. On mobile it moves down to
              sit directly above the track list (LUT-881), giving its line to
              the purchase block so Buy clears the fold. */}
          <Flex justify="between" align="center" className="hidden lg:block">
            <Flex align="center" className="gap-1.5">
              <Text className="text-[12px] font-normal leading-[140%] text-white/60">
                {albumTrackCountLabel(albumData, { capitalize: true })} &bull;{" "}
                {formatTotalDuration(albumData.tracks)} Minutes
              </Text>
            </Flex>
          </Flex>

          {/* Purchase info: subtitle + buy button */}
          {showPurchaseBlock && (
            <Flex direction="column" className="gap-4 my-[23px] lg:my-0">
              <Flex direction="column" className="gap-2">
                {isPreRelease ? (
                  <PreReleaseBlock
                    releaseDate={albumData.releaseDate!}
                    albumId={albumData.albumId}
                    onExpired={handlePreReleaseExpired}
                  />
                ) : isOwned ? (
                  <>
                    <Button
                      size="3"
                      radius="full"
                      className="w-full"
                      onClick={handleDownload}
                      loading={isDownloading}
                    >
                      <HiOutlineDownload className="w-5 h-5" />
                      Download
                    </Button>
                    <InAppBrowserDownloadNotice subject="album" />
                    {albumData.medias
                      ?.filter((media) => media.mediaType === "VIDEO")
                      .map((media) => (
                        <Button
                          key={media.albumMediaId}
                          size="3"
                          radius="full"
                          className="w-full"
                          onClick={() =>
                            setVideoModal({
                              src: media.mediaUrl,
                              title: albumData.title,
                            })
                          }
                        >
                          <MdMovie className="w-5 h-5" />
                          Watch Video
                        </Button>
                      ))}
                    {purchaseDate && (
                      <Text className="text-[12px] font-normal leading-[140%] text-white/60 text-center">
                        Purchased on {purchaseDate}
                      </Text>
                    )}
                  </>
                ) : (
                  <>
                    <Flex direction="column" className="gap-1.5">
                      <Text className="text-[12px] font-semibold leading-[140%] text-white/80">
                        Name Your Price
                      </Text>
                      {/* Price pills */}
                      <Flex className="gap-1.5 items-center flex-wrap">
                        {presetTiers.map((tier) => (
                          <button
                            key={tier}
                            type="button"
                            data-testid="public-album-price-tier"
                            onClick={() => handleSelectTier(tier)}
                            className={`h-[40px] px-4 rounded-full text-[14px] font-semibold transition-colors cursor-pointer ${
                              selectedPrice === tier && !isCustomMode
                                ? "bg-white text-black"
                                : "bg-white/15 text-white hover:bg-white/25"
                            }`}
                          >
                            ${tier}
                          </button>
                        ))}
                        {isCustomMode && customInputFocused ? (
                          <CurrencyInput
                            value={customPriceValue}
                            onChange={handleCustomPriceChange}
                            onBlur={handleCustomPriceBlur}
                            placeholder={minPrice.toFixed(2)}
                            min={minPrice}
                            autoFocus
                            variant="pill"
                            className="w-[100px] h-[40px] shrink-0"
                            inputClassName="!h-[40px]"
                          />
                        ) : isCustomMode && customPriceValue ? (
                          <button
                            type="button"
                            onClick={handleCustomPillClick}
                            className={`h-[40px] px-4 rounded-full text-[14px] font-semibold transition-colors cursor-pointer ${
                              customPriceError
                                ? "bg-red-500/80 text-white"
                                : "bg-white text-black"
                            }`}
                          >
                            ${customPriceValue}
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid="public-album-price-other"
                            onClick={handleEnterCustomMode}
                            className="h-[40px] px-4 rounded-full text-[14px] font-semibold bg-white/15 text-white hover:bg-white/25 transition-colors cursor-pointer"
                          >
                            Other
                          </button>
                        )}
                      </Flex>
                      <Tooltip
                        content="Select a price to continue"
                        side="bottom"
                        open={
                          selectedPrice == null && !isInCart ? undefined : false
                        }
                      >
                        <Button
                          size="3"
                          radius="full"
                          className={`w-full !h-[40px] ${selectedPrice == null && !isInCart ? "!cursor-default" : "!cursor-pointer"}`}
                          data-testid="public-album-buy-button"
                          onClick={handleAddToCart}
                          disabled={selectedPrice == null && !isInCart}
                        >
                          <HiOutlineShoppingBag className="w-5 h-5 shrink-0" />
                          {isInCart
                            ? "In Cart"
                            : selectedPrice != null
                              ? `Buy ${albumTypeLabel(albumData)}${albumData.extraMedia ? ` + ${albumData.extraMedia}` : ""} $${selectedPrice.toFixed(2)}`
                              : `Buy ${albumTypeLabel(albumData)}${albumData.extraMedia ? ` + ${albumData.extraMedia}` : ""}`}
                        </Button>
                      </Tooltip>
                      <Flex className="gap-2 items-center flex-wrap">
                        <Text className="text-[11px] font-normal leading-[140%] text-white/50">
                          {minPrice > 0
                            ? `Minimum: $${minPrice.toFixed(2)}`
                            : "Minimum: Free"}
                        </Text>
                        {customPriceError && (
                          <Text className="text-[11px] font-normal leading-[140%] text-red-400">
                            {customPriceError}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                    <Flex direction="column" className="gap-0.5">
                      <Text className="text-[12px] font-normal leading-[140%] text-white/60 text-center">
                        High quality MP3 download + Unlimited streaming on
                        Lutely
                      </Text>
                      <Text className="text-[12px] font-normal leading-[140%] text-white/60 text-center">
                        Watermark is removed after purchase.
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>
          )}

          {/* Mobile home of the tracks info row: directly above the track
              list, where the count describes what follows. */}
          <Text className="lg:hidden text-[12px] font-normal leading-[140%] text-white/60">
            {albumTrackCountLabel(albumData, { capitalize: true })} &bull;{" "}
            {formatTotalDuration(albumData.tracks)} Minutes
          </Text>

          {/* Tabs */}
          <Tabs.Root defaultValue="tracklist">
            <Tabs.List className="flex w-full gap-2 overflow-x-auto no-scrollbar">
              <Tabs.Trigger
                value="tracklist"
                className="flex-1 text-center px-4 py-2 text-[16px] font-semibold leading-[140%] text-white/80 whitespace-nowrap cursor-pointer transition-colors duration-150 hover:text-white data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#43DDA7]"
              >
                Tracklist
              </Tabs.Trigger>
              <Tabs.Trigger
                value="details"
                className="flex-1 text-center px-4 py-2 text-[16px] font-semibold leading-[140%] text-white whitespace-nowrap cursor-pointer transition-colors duration-150 hover:text-white data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#43DDA7] [text-shadow:0_2px_8px_rgba(0,0,0,0.45)]"
              >
                Details
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content
              value="tracklist"
              forceMount
              className="data-[state=inactive]:hidden"
            >
              <AlbumTracklist
                tracks={albumData.tracks}
                previewMode={!isOwned}
                noPlayback={isPreRelease}
                versionsAsTracks={
                  albumReleaseType(albumData) === "SUPER_SINGLE"
                }
                creatorId={albumData.creator.creatorId}
                creatorName={creatorDisplayName}
                artworkUrl={albumData.artworkUrl ?? undefined}
                onLyricsClick={handleLyricsClick}
                lyricsLocked={!isOwned && !canEditAlbum}
                onShareTrack={(track) =>
                  share({
                    // Tracks that only exist inside the album have no public
                    // page of their own — share the album instead.
                    url: track.published
                      ? `${SITE_URL}/tracks/${track.trackId}`
                      : albumShareUrl,
                    title: track.title || "Untitled",
                    kind: "track",
                    artistName: creatorDisplayName,
                    artworkUrl: track.imageUrl || albumData.artworkUrl,
                  })
                }
                shareDisabled={!albumData.publishedAt}
              />
            </Tabs.Content>

            <Tabs.Content
              value="details"
              forceMount
              className="data-[state=inactive]:hidden"
            >
              <AlbumDetailsTab
                description={albumData.description}
                genreDisplayText={genreDisplayText}
                publishedAt={albumData.publishedAt}
                releaseDate={albumData.releaseDate}
                trackCount={albumData.tracks.length}
                releaseType={albumReleaseType(albumData)}
                credits={albumData.credits}
              />
            </Tabs.Content>
          </Tabs.Root>

          <AlbumLyricsDrawer
            trackId={lyricsFor?.trackId ?? null}
            clean={lyricsFor?.clean}
            trackTitle={lyricsTrackTitle}
            onClose={() => setLyricsFor(null)}
          />
          <VideoPlayerModal
            open={!!videoModal}
            src={videoModal?.src ?? ""}
            title={videoModal?.title}
            onClose={() => setVideoModal(null)}
          />
          {shareModal}
        </Flex>
      </div>
    </ContainerWrapper>
  );
}
